import type { Correction } from "@/queries/formality-request/types";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { cn } from "@/utils/theme";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import expedientRenderer from "../common/expedient";
import dateRangeRenderer from "./renderers/date-range";
import fileRenderer from "../common/file";
import numberRenderer from "../common/number";
import phoneRenderer from "../common/phone";
import tableRenderer from "../common/table";
import ReadOnlyCorrection from "./corrections/read-only-correction";
import EditableCorrection from "./corrections/editable-correction";
import datetimeRangeRenderer from "../common/datetime-range";
import duiNitRenderer from "../common/dui-nit";
import filteredSelectRenderer from "../common/filtered-select";
import selectRenderer from "../common/select";
import switchRenderer from "../common/switch";
import { getLastFilledField } from "@/utils/filled-fields";

type FilledField = {
  id: string;
  value: unknown;
  updated_at: string;
  correction?: unknown;
  label?: string | null;
  unique_code?: string | null;
  name?: string | null;
  files?: Array<{ id: string; url: string; file_name: string }>;
};

type RenderProps = {
  obj: Fields;
  value: unknown;
  targetField: FilledField | undefined;
  fieldArr: FilledField[] | null;
};

const renderers: Record<string, (p: RenderProps) => React.ReactNode> = {
  switch: switchRenderer,
  expedient: expedientRenderer,
  datetime_range: datetimeRangeRenderer,
  dateRange: dateRangeRenderer,
  date_range: dateRangeRenderer,
  select: selectRenderer,
  file: fileRenderer,
  dui_nit: duiNitRenderer,
  filtered_select: filteredSelectRenderer,
  "filtered-list": filteredSelectRenderer,
  filtered_list: filteredSelectRenderer,
  number: numberRenderer,
  phone: phoneRenderer,
  table: tableRenderer,
};

export type Fields =
  FormDetailResponse["steps"][number]["sections"][number]["fields"][number];

const columnHelper = createColumnHelper<Fields>();
export const tableColumns = ({
  corrections,
  onCorrection,
  onOpenCorrectionModal,
  onShowFieldHistory,
  currentGroupId,
  lockedApprovedFields,
  isReadOnly = false,
  existingCorrection,
  groupsCount,
  nonPreventableFields,
  // `fieldStartIndex` ya no se desestructura: el diseno quito la columna con el
  // numero de fila. Se conserva en el tipo porque los llamadores lo siguen
  // enviando y lo usa la tabla del detalle, que si numera sus filas.
}: Readonly<{
  onOpenCorrectionModal: (fieldId: string) => void;
  onCorrection: (fieldId: string, status: "DENIED" | "APPROVED") => void;
  onShowFieldHistory: (field: Fields) => void;
  corrections: Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>;
  currentGroupId?: string | null;
  lockedApprovedFields?: Set<string>;
  nonPreventableFields?: Set<string>;
  isReadOnly?: boolean;
  existingCorrection?: Correction;
  groupsCount: number;
  fieldStartIndex?: number;
}>) => {
  return [
    columnHelper.accessor("name", {
      header: "Tipo de dato",
      meta: {
        className:
          "align-top min-w-0 w-[45%] max-w-[50%] lg:w-[353px] lg:min-w-[353px] lg:max-w-[353px] !whitespace-normal break-words overflow-hidden",
      },
      cell: (info) => {
        if (!info.getValue()) {
          return <p>&nbsp;</p>;
        }
        const field = info.row.original;
        return (
          // El tipo de campo no se muestra: la celda lleva "Requerido" u
          // "Opcional" arriba y el nombre debajo.
          <div className="w-full flex flex-col gap-0.5 min-w-0">
            <span
              className={
                field.required
                  ? "text-info text-xs font-semibold"
                  : "text-body-quiet text-xs"
              }
            >
              {field.required ? "Requerido" : "Opcional"}
            </span>
            <p className="font-semibold text-body-loud text-size-body wrap-break-words min-w-0">
              {field.label}
            </p>
            {!field?.is_preventable && (
              <p className="text-warning font-normal text-xs">(No Observable)</p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("filled_fields", {
      header: "Valor",
      meta: {
        className:
          "w-full overflow-hidden !whitespace-normal break-all align-center",
      },
      cell: (info) => {
        const fieldArr = info.getValue();
        const obj = info.row.original;
        const field = info.row.original;
        if ((!fieldArr || fieldArr.length === 0) && obj.type !== "switch") {
          return <span className="text-on-disabled-quiet">Vacío</span>;
        }

        const targetField = getLastFilledField(fieldArr);
        const value = targetField?.value;

        const defaultContent: React.ReactNode = (
          <p className="wrap-break-word whitespace-pre-wrap text-size-body">
            {value?.toString() ?? ""}
          </p>
        );

        let content: React.ReactNode;
        if (field.type === "interoperability") {
          const count = info.row.original.config.input_field.length || 0;
          const lastValue = fieldArr?.slice(-count);
          content = (
            <div className="flex flex-col gap-2">
              {lastValue?.map((filledField, index) => (
                <div
                  key={`${filledField.id}-${index}`}
                  className="flex flex-col"
                >
                  <p className="text-xs text-info font-semibold">
                    {filledField.label}:
                  </p>
                  <p className="break-normal whitespace-pre-wrap text-size-body">
                    {filledField.value === null ||
                    filledField.value === undefined ||
                    filledField.value === "" ? (
                      <span className="text-on-disabled-quiet">Vacío</span>
                    ) : (
                      filledField.value.toString()
                    )}
                  </p>
                </div>
              ))}
            </div>
          );
        } else {
          const renderer = renderers[obj.type];
          if (renderer) {
            content = renderer({ obj, value, targetField, fieldArr });
          } else if (Array.isArray(value) && value.length > 0) {
            content = (
              <p className="wrap-break-word whitespace-pre-wrap text-size-body">
                {((value as Array<string>) || []).join(", ")}
              </p>
            );
          } else if (
            value === null ||
            value === undefined ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
          ) {
            content = <span className="text-on-disabled-quiet">Vacío</span>;
          } else {
            content = defaultContent;
          }
        }

        return (
          <div className="flex w-full items-center gap-2">
            <div className="min-w-0 flex-1">{content}</div>
            <Button
              size="icon"
              variant="quiet"
              type="button"
              aria-label="correction-history"
              className="shrink-0"
              onClick={() => onShowFieldHistory(field)}
            >
              <Icon name="clock" type="far" />
            </Button>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-[48px] border-transparent border-x-2 align-center",
      },
      cell: (info) => {
        const filledFields = info.row.original.filled_fields;
        const isPreventable = info.row.original.is_preventable;

        const targetField = getLastFilledField(filledFields);
        const filledFieldId = targetField?.id;

        if (nonPreventableFields?.has(filledFieldId || "") || !isPreventable) {
          return;
        }

        // Read-only mode — show existing correction status
        if (isReadOnly && existingCorrection) {
          const fieldCorrection = filledFieldId
            ? existingCorrection.field_corrections?.find(
                (fc) => fc.filled_field_id === filledFieldId,
              )
            : undefined;
          if (!fieldCorrection) return null;
          return (
            <ReadOnlyCorrection
              fieldId={filledFieldId}
              correction={fieldCorrection}
              onOpenModal={onOpenCorrectionModal}
            />
          );
        }

        // Editable mode — show approve/deny buttons
        const fieldIdentifier =
          filledFieldId || `field_${info.row.original.id}`;
        const hasPreviousCorrectionApproved =
          lockedApprovedFields?.has(filledFieldId || "") || false;
        const isLockedApproved =
          currentGroupId && groupsCount > 1
            ? false
            : (lockedApprovedFields?.has(filledFieldId || "") ?? false);
        const correction = corrections.get(fieldIdentifier);

        return (
          <div
            className={cn(
              "flex gap-3",
              hasPreviousCorrectionApproved && "justify-start",
            )}
          >
            <EditableCorrection
              isLockedApproved={isLockedApproved}
              hasPreviousCorrectionApproved={hasPreviousCorrectionApproved}
              correction={correction}
              onApprove={() => onCorrection(fieldIdentifier, "APPROVED")}
              onDeny={() => onCorrection(fieldIdentifier, "DENIED")}
              onOpenModal={() => onOpenCorrectionModal(fieldIdentifier)}
            />
          </div>
        );
      },
    }),
  ] as ColumnDef<Fields>[];
};
